import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faUserGroup } from "@fortawesome/free-solid-svg-icons";
import AmountInput from "../../../Components/Common/AmountInput";
import CurrencyRadioGroup from "../../../Components/Common/CurrencyRadioGroup";
import FormInput from "../../../Components/Common/FormInput";
import {
  SUYOOL_LOOKUP_MESSAGES,
  useRecipientNameLookup,
} from "../../../Components/S2STransferForms";
import { CURRENCY_IDS } from "../../../constants/currency";

const INDIVIDUAL_TYPE_ID = 2;

const SEND_MODE_OPTIONS = [
  { value: "one", label: "One" },
  { value: "bulk", label: "2 or more" },
];

const RECIPIENT_TYPE_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "business", label: "Business" },
];

const CURRENCY_OPTIONS = [
  { value: String(CURRENCY_IDS.USD), label: "USD" },
  { value: String(CURRENCY_IDS.LBP), label: "LBP" },
];

export default function MakePaymentForm({
  amount,
  currencyId,
  errors,
  onChange,
  phoneNumber,
  recipientType,
  sendMode,
}) {
  const isUsd = Number(currencyId) === CURRENCY_IDS.USD;
  const isIndividual = recipientType !== "business";

  const { resolvedName, checkPhoneNumber } = useRecipientNameLookup(
    INDIVIDUAL_TYPE_ID,
  );
  const [isNotSuyoolUser, setIsNotSuyoolUser] = useState(false);
  const checkPhoneNumberRef = useRef(checkPhoneNumber);
  checkPhoneNumberRef.current = checkPhoneNumber;

  useEffect(() => {
    if (!isIndividual) return;

    let cancelled = false;

    checkPhoneNumberRef.current(phoneNumber).then((result) => {
      if (cancelled) return;
      setIsNotSuyoolUser(result?.message === SUYOOL_LOOKUP_MESSAGES.notSuyoolUser);
    });

    return () => {
      cancelled = true;
    };
  }, [phoneNumber, isIndividual]);

  const recipientNameDisplay = isNotSuyoolUser
    ? "The user is not on Suyool"
    : resolvedName ||
      "Recipient Name will appear here if they have a Suyool Account";

  return (
    <div className="make-payment-form">
      <CurrencyRadioGroup
        label="Send money to"
        name="makePaymentSendMode"
        value={sendMode}
        onChange={(value) => onChange("sendMode", value)}
        options={SEND_MODE_OPTIONS}
      />

      {sendMode === "one" ? (
        <>
          <CurrencyRadioGroup
            label="Recipient Type"
            name="makePaymentRecipientType"
            value={recipientType}
            onChange={(value) => onChange("recipientType", value)}
            options={RECIPIENT_TYPE_OPTIONS}
          />

          {isIndividual ? (
            <div className="make-payment-form__recipient-grid">
              <FormInput
                label="Recipient Phone Number"
                value={phoneNumber}
                placeholder="+961 71 123 456"
                inputMode="numeric"
                pattern="[0-9]*"
                prefix={<FontAwesomeIcon icon={faPhone} />}
                error={errors.phoneNumber}
                onChange={(event) =>
                  onChange("phoneNumber", event.target.value.replace(/\D/g, ""))
                }
              />

              <div className="make-payment-form__recipient-name">
                <label className="make-payment-form__recipient-name-label">
                  Recipient Name
                </label>
                <div
                  className={`make-payment-form__readonly ${
                    isNotSuyoolUser ? "is-warning" : ""
                  }`}
                >
                  <FontAwesomeIcon icon={faUserGroup} />
                  <span>{recipientNameDisplay}</span>
                </div>
              </div>
            </div>
          ) : null}

          <CurrencyRadioGroup
            label="From Account"
            name="makePaymentCurrency"
            value={String(currencyId)}
            onChange={(value) => onChange("currencyId", Number(value))}
            options={CURRENCY_OPTIONS}
            error={errors.currencyId}
          />

          <AmountInput
            label={`Amount (${isUsd ? "USD" : "LBP"})`}
            prefix={isUsd ? "$" : "L.L"}
            value={amount}
            placeholder={isUsd ? "0.00" : "0"}
            error={errors.amount}
            clearZeroOnFocus
            onChange={(event) => onChange("amount", event.target.value)}
          />
        </>
      ) : null}
    </div>
  );
}
